// projectValidation.ts
import * as Yup from "yup";
import { DocumentStatus, ProjectType, type ProjectSaleableServiceGroup } from "@repo/server-api";
import type { TFunction } from "i18next";
import type {
	NewProject,
	Project,
	ProjectAccountingInformation,
	ProjectContactInformation,
	ProjectContactPersonInformation,
	ProjectPosition,
	ProjectTeamMember,
	ProjectSaleableService,
	SelectedEmployee,
	WorksOnPosition,
} from "./ProjectTypes";
import type { IdWithName } from "@repo/shared/common";

// --- Helper ------------------------------------------------------------------

const requiredString = (t: TFunction, key: string) =>
	Yup.string()
		.transform((value) => (typeof value === "string" ? value.trim() : value))
		.min(1, t(key, { ns: "validation" }))
		.required(t(key, { ns: "validation" }));

const nullableNumber = () => Yup.number().nullable().defined();

function nullableObject<TType extends {}>() {
	return Yup.mixed<TType>().nullable().defined();
}

function nullableString() {
	return Yup.string()
		.transform((_value, originalValue) => {
			// string | null → allow null OR any string (including empty)
			if (originalValue === null || originalValue === undefined) {
				return null;
			}
			return typeof originalValue === "string" ? originalValue : String(originalValue);
		})
		.nullable()
		.defined();
}

const requiredNumber = (t: TFunction, key: string) =>
	Yup.number()
		.typeError(t(key, { ns: "validation" }))
		.required(t(key, { ns: "validation" }));

// --- Schema Factory ----------------------------------------------------------

export const createSelectedServiceSchema = (
	t: TFunction,
): Yup.ObjectSchema<ProjectSaleableService> =>
	createIdWithNameSchema(t).shape({
		price: requiredNumber(t, "price-is-required"),
	});

export const createIdWithNameSchema = (t: TFunction): Yup.ObjectSchema<IdWithName> =>
	Yup.object({
		id: requiredString(t, "id-is-required"),
		name: requiredString(t, "name-is-required"),
	});

// complex nested types
export const createProjectPositionSchema = (t: TFunction): Yup.ObjectSchema<ProjectPosition> => {
	return Yup.object({
		id: requiredString(t, "position-id-is-required"),
		// ProjectPosition must have a service
		service: createSelectedServiceSchema(t).required(
			t("service-is-required", { ns: "validation" }),
		),
		hours: nullableNumber(),
		pricePerHour: nullableNumber(),
		price: nullableNumber(),
		priceRate: nullableString(),
		description: nullableString(),
		accountingProfile: Yup.mixed<IdWithName>().defined().nullable(),
		totalPrice: nullableNumber(),
	});
};

export const createWorksOnPositionSchema = (
	t: TFunction,
): Yup.ObjectSchema<WorksOnPosition> => {
	return Yup.object({
		positionId: requiredString(t, "position-id-is-required"),
		pricePerHour: Yup.number()
			.nullable()
			.min(0, t("must-be-non-negative", { ns: "validation" }))
			.defined(),
		hours: Yup.number()
			.nullable()
			.test(
				"greater-than-zero",
				t("must-be-greater-than-zero", { ns: "validation" }),
				(value) => value === null || value === undefined || value > 0,
			)
			.defined(),
	});
};

export const createProjectTeamMemberSchema = (
	t: TFunction,
): Yup.ObjectSchema<ProjectTeamMember> => {
	return Yup.object({
		id: nullableString(),
		teamMember: nullableObject<SelectedEmployee>(),
		contractor: Yup.mixed<IdWithName>()
			.defined()
			.nullable()
			.when("teamMember", {
				is: (teamMember: SelectedEmployee | null) => teamMember != null,
				then: (schema) => schema.required(t("contractor-required", { ns: "validation" })),
				otherwise: (schema) => schema,
			}),
		worksOnPositions: Yup.array()
			.of(createWorksOnPositionSchema(t).required())
			.required()
			.min(1, t("at-least-one-position-is-required", { ns: "validation" })),
	});
};

export const createProjectSchema = (t: TFunction): Yup.ObjectSchema<Project> => {
	const accountingInformationSchema: Yup.ObjectSchema<ProjectAccountingInformation> = Yup.object({
		id: nullableString(),
		accountingProfile: nullableObject<IdWithName>(),
		clientContractNumber: nullableString(),
		clientContractDate: nullableString(),
		clientOrderingNumber: nullableString(),
		clientOrderingDate: nullableString(),
		provision: nullableNumber(),
		clientProjectNumber: nullableString(),
		supplierProjectNumber: nullableString(),

		timeTrackingResolution: Yup.number()
			.required(t("time-tracking-resolution-is-required", { ns: "validation" }))
			.min(1, t("must-be-between-1-and-60", { ns: "validation" }))
			.max(60, t("must-be-between-1-and-60", { ns: "validation" }))
			.test("divides-60", t("must-be-divisor-of-60", { ns: "validation" }), function (value) {
				if (value == null) return true;
				return Number.isInteger(60 / value);
			}),
	})
		.defined()
		.required();

	return Yup.object({
		id: requiredString(t, "id-is-required"),

		// required strings
		name: requiredString(t, "name-is-required"),
		number: requiredString(t, "number-is-required"),
		parentProject: nullableObject<IdWithName>(),

		status: Yup.mixed<DocumentStatus>().required(),
		projectType: Yup.mixed<ProjectType>().required(),

		startDate: requiredString(t, "start-date-is-required"),
		endDate: requiredString(t, "end-date-is-required"),

		isDeleted: Yup.boolean().required(),

		contactInformation: Yup.mixed<ProjectContactInformation>().required(),
		contactPersonInformation: Yup.mixed<ProjectContactPersonInformation>().required(),
		accountingInformation: accountingInformationSchema,
		TemplateInformation: Yup.mixed().required(),

		budgetHours: Yup.number().required(),
		budgetPrice: Yup.number().required(),

		hasBudgetHours: Yup.boolean().required(),
		hasBudgetPrice: Yup.boolean().required(),

		saleableServiceGroups: Yup.array<ProjectSaleableServiceGroup>().required(),
		saleableServices: Yup.array<ProjectSaleableService>().required(),
		saleableUnits: Yup.array<IdWithName>().required(),

		positions: Yup.array<ProjectPosition>().required(),
		teamMembers: Yup.array<ProjectTeamMember>().required(),
	});
};

export const createNewProjectSchema = (t: TFunction): Yup.ObjectSchema<NewProject> => {
	// primitive nested types
	const idWithNameSchema: Yup.ObjectSchema<IdWithName> = Yup.object({
		id: requiredString(t, "id-is-required"),
		name: requiredString(t, "name-is-required"),
	});

	const newProjectSchema: Yup.ObjectSchema<NewProject> = Yup.object({
		// required strings
		name: requiredString(t, "name-is-required"),

		type: Yup.mixed<ProjectType>()
			.oneOf(
				Object.values(ProjectType) as ProjectType[],
				t("project-type-is-required", { ns: "validation" }),
			)
			.required(t("project-type-is-required", { ns: "validation" })),

		startDate: requiredString(t, "start-date-is-required"),
		endDate: requiredString(t, "end-date-is-required"),

		parentProject: idWithNameSchema.nullable(),
		contractor: idWithNameSchema.required(t("contractor-is-required", { ns: "validation" })),
		client: idWithNameSchema
			.nullable()
			.when("type", {
				is: (type: ProjectType) => type !== ProjectType.Internal,
				then: (schema) => schema.required(t("client-is-required", { ns: "validation" })),
				otherwise: (schema) => schema.nullable(),
			}),
	});

	return newProjectSchema;
};
